import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { RegMark } from "./BlueprintBg";
import { ASSETS, NEW_SITE_URL, NEW_DOMAIN } from "../config";

// Falling manifest chips (squares/diamonds) — celebratory but on-brand.
const chips = Array.from({ length: 34 }, (_, i) => ({
  left: (i * 29) % 100,
  delay: (i % 12) * 0.12,
  duration: 3.2 + (i % 6) * 0.5,
  accent: i % 2 === 0,
  size: 6 + (i % 4) * 2,
  diamond: i % 3 === 0,
}));

export const PostLaunch = () => {
  const { t } = useLanguage();

  return (
    <section
      className="relative flex min-h-screen items-center justify-center overflow-hidden border-b bp-border pt-16"
      data-testid="post-launch-section"
    >
      {/* falling chips */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {chips.map((c, i) => (
          <motion.span
            key={i}
            className={`absolute top-[-6%] ${c.diamond ? "rotate-45" : ""}`}
            style={{
              left: `${c.left}%`,
              width: c.size,
              height: c.size,
              background: c.accent ? "var(--xp-accent)" : "var(--xp-primary)",
            }}
            initial={{ y: "-10vh", opacity: 0 }}
            animate={{ y: "115vh", opacity: [0, 1, 1, 0], rotate: c.diamond ? 405 : 180 }}
            transition={{ duration: c.duration, delay: c.delay, repeat: Infinity, ease: "linear" }}
          />
        ))}
      </div>

      <div className="relative z-10 mx-auto w-full max-w-5xl px-4 md:px-8">
        <div
          className="relative border-2 p-8 md:p-16"
          style={{ borderColor: "var(--xp-primary)", background: "var(--xp-surface)" }}
        >
          <RegMark className="left-3 top-3" />
          <RegMark className="right-3 top-3" />
          <RegMark className="bottom-3 left-3" />
          <RegMark className="bottom-3 right-3" />

          {/* status stamp */}
          <motion.div
            initial={{ scale: 1.6, opacity: 0, rotate: -12 }}
            animate={{ scale: 1, opacity: 1, rotate: -8 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="absolute right-6 top-6 hidden border-2 px-3 py-1 font-mono-x text-xs font-bold uppercase tracking-[0.2em] md:block"
            style={{ borderColor: "var(--xp-accent)", color: "var(--xp-accent)" }}
          >
            ● {t.launched.badge}
          </motion.div>

          <motion.img
            src={ASSETS.newLogoIcon}
            alt="XPDC"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-8 h-14 w-14 object-contain md:h-16 md:w-16"
          />

          <p className="mb-4 font-mono-x text-[10px] uppercase tracking-[0.3em] text-muted-x">
            STATUS // {NEW_DOMAIN}
          </p>

          <h1 className="font-display text-5xl uppercase leading-[0.88] tracking-tighter sm:text-6xl lg:text-8xl">
            {t.launched.title.split(" ").map((word, i) => (
              <span key={i} className="inline-block overflow-hidden">
                <motion.span
                  initial={{ y: "108%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.8, delay: 0.5 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                  className="mr-3 inline-block"
                  style={i === 1 ? { color: "var(--xp-primary)" } : undefined}
                >
                  {word}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-muted-x md:text-lg"
          >
            {t.launched.desc}
          </motion.p>

          <motion.a
            href={NEW_SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="post-launch-cta-button"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.2 }}
            whileHover={{ x: 6 }}
            className="group mt-12 inline-flex items-center gap-4 px-8 py-5 font-display text-lg uppercase tracking-tight text-white md:text-xl"
            style={{ background: "var(--xp-primary)" }}
          >
            {t.launched.cta} {NEW_DOMAIN}
            <ArrowUpRight className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </motion.a>

          <p className="mt-8 font-mono-x text-[10px] uppercase tracking-[0.3em] text-muted-x">
            {t.launched.celebrate}
          </p>
        </div>
      </div>
    </section>
  );
};
